import {Component, Property, Texture} from "@wonderlandengine/api";

export const DEFAULT_VIDEO_URL = "video.mp4";
export const DEFAULT_TEXTURE_PROPERTY = "flatTexture";

function findObjectByName(engine, name) {
  if (!name) return null;

  if (engine.scene?.findByName) {
    const result = engine.scene.findByName(name, true);
    return Array.isArray(result) ? result[0] : result;
  }

  const roots = engine.scene?.children || engine.scene?.root?.children || [];
  const stack = [...roots];
  while (stack.length) {
    const object = stack.shift();
    if (object?.name === name) return object;
    if (object?.children) stack.push(...object.children);
  }

  return null;
}

function resolveObject(engine, objectOrName) {
  if (objectOrName?.getComponent) return objectOrName;
  return findObjectByName(engine, objectOrName);
}

function getObjectLabel(objectOrName) {
  return typeof objectOrName === "string"
    ? objectOrName
    : objectOrName?.name || "unnamed";
}

function getBoardMaterial(object, label) {
  const mesh = object.getComponent("mesh");
  if (!mesh?.material) {
    console.warn(`video-texture-board: object "${label}" needs a mesh.`);
    return null;
  }

  const material = mesh.material.clone ? mesh.material.clone() : mesh.material;
  mesh.material = material;
  return material;
}

function setMaterialTexture(
  material,
  texture,
  textureProperty = DEFAULT_TEXTURE_PROPERTY,
) {
  if (textureProperty && textureProperty !== "auto") {
    material[textureProperty] = texture;
    return true;
  }

  const candidates =
    material.shader === "Flat Opaque Textured"
      ? [DEFAULT_TEXTURE_PROPERTY]
      : [
          DEFAULT_TEXTURE_PROPERTY,
          "diffuseTexture",
          "mainTexture",
          "texture",
          "albedoTexture",
        ];

  for (const property of candidates) {
    try {
      material[property] = texture;
      return true;
    } catch {
      continue;
    }
  }

  return false;
}

function addUserGesturePlaybackFallback(video, label) {
  if (typeof window === "undefined") return;

  const playVideo = () => {
    if (!video.src && !video.srcObject) return;
    video.play().catch((err) => {
      console.warn(`video-texture-board: playback failed for "${label}".`, err);
    });
  };

  window.addEventListener("click", playVideo, {once: true});
  window.addEventListener("touchstart", playVideo, {once: true});
  window.addEventListener("pointerdown", playVideo, {once: true});
}

function createVideoBoardState(engine, objectOrName, video, options = {}) {
  const object = resolveObject(engine, objectOrName);
  const label = getObjectLabel(objectOrName);
  if (!object) {
    console.warn(`video-texture-board: object "${label}" was not found.`);
    return null;
  }

  const material = getBoardMaterial(object, label);
  if (!material) return null;

  return {
    object,
    label,
    video,
    material,
    texture: null,
    textureProperty: options.textureProperty || DEFAULT_TEXTURE_PROPERTY,
    destroyed: false,

    update() {
      if (this.destroyed || !this.video || this.video.readyState < 2) return;

      if (!this.texture) {
        this.texture = new Texture(engine, this.video);
        if (!setMaterialTexture(material, this.texture, this.textureProperty)) {
          console.error(
            `video-texture-board: failed to set texture on "${label}".`,
          );
          return;
        }
      }

      this.texture.update();
    },

    play() {
      return this.video.play();
    },

    pause() {
      this.video.pause();
    },

    togglePlay() {
      if (this.video.paused) {
        this.video.play().catch((err) => {
          console.warn(
            `video-texture-board: play failed for "${label}".`,
            err,
          );
        });
        return true;
      }

      this.video.pause();
      return false;
    },

    replay() {
      this.video.currentTime = 0;
      this.video.play().catch((err) => {
        console.warn(`video-texture-board: replay failed for "${label}".`, err);
      });
    },

    isPaused() {
      return this.video.paused;
    },

    stop() {
      this.destroyed = true;
      this.video.pause();
      if (this.video.srcObject) this.video.srcObject = null;
      this.video.removeAttribute("src");
      this.video.load?.();
    },
  };
}

export class VideoTextureBoard extends Component {
  static TypeName = "video-texture-board";
  static Properties = {
    url: Property.string(DEFAULT_VIDEO_URL),
    textureProperty: Property.string(DEFAULT_TEXTURE_PROPERTY),
    autoplay: Property.bool(true),
    loop: Property.bool(true),
    muted: Property.bool(true),
  };

  start() {
    this.board = createVideoTextureOnObject(this.engine, this.object, {
      url: this.url,
      textureProperty: this.textureProperty,
      autoplay: this.autoplay,
      loop: this.loop,
      muted: this.muted,
    });
  }

  update() {
    this.board?.update();
  }
}

export function createVideoTextureOnObject(engine, objectOrName, options = {}) {
  const video = document.createElement("video");
  const label = getObjectLabel(objectOrName);
  const crossOrigin = options.crossOrigin ?? "anonymous";

  if (crossOrigin) video.crossOrigin = crossOrigin;
  video.preload = options.preload || "auto";
  video.playsInline = true;
  video.loop = options.loop ?? true;
  video.muted = options.muted ?? true;
  video.src = options.url || DEFAULT_VIDEO_URL;

  const state = createVideoBoardState(engine, objectOrName, video, options);
  if (!state) {
    video.removeAttribute("src");
    video.load?.();
    return null;
  }

  if (options.autoplay ?? true) {
    video.play().catch((err) => {
      console.warn(`video-texture-board: autoplay failed for "${label}".`, err);
    });
    addUserGesturePlaybackFallback(video, label);
  }

  return state;
}

export function createStreamTextureOnObject(
  engine,
  objectOrName,
  stream,
  options = {},
) {
  const video = document.createElement("video");
  const label = getObjectLabel(objectOrName);

  video.srcObject = stream;
  video.playsInline = true;
  video.muted = options.muted ?? true;

  const state = createVideoBoardState(engine, objectOrName, video, options);
  if (!state) {
    video.pause();
    video.srcObject = null;
    return null;
  }

  video.play().catch((err) => {
    console.warn(
      `video-texture-board: stream playback failed for "${label}".`,
      err,
    );
  });
  addUserGesturePlaybackFallback(video, label);

  return state;
}
